import { Grid, Typography } from '@material-ui/core';
import { usePagination } from '@ncr-retail/hooks';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { codeContainerStyles } from 'src/common/components/code-container/components/code-container-styles';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { truncateNumberWithPrecision } from 'src/common/helpers/number-helper';
import { ITransactionListing } from 'src/common/helpers/transaction-helper';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import Pagination from 'src/pagination/pagination';
import { PosFilterAction } from './enumerations/pos-filter-action';
import FilterTextField from './filter-text-field';
import { SavedTransactionCategoryHead } from './saved-transaction-category-head';
import { SavedTransactionListings } from './saved-transactions-list';

const pageSize = 6;

export const sortSavedTransactions = (
  transactionList: ITransactionListing[],
): ITransactionListing[] => {
  const convertedTimeList: ITransactionListing[] = transactionList.map(carts => {
    return {
      ...carts,
      suspendTime: moment(carts.suspendTime)
        .local()
        .format(),
    };
  });
  return convertedTimeList.sort(
    (date1, date2) => new Date(date2.suspendTime).valueOf() - new Date(date1.suspendTime).valueOf(),
  );
};

export const showSavedTransactionsScreen = (props: SavedTransactionsScreenProps): void => {
  setHomePage(<SavedTransactions {...props} />);
};

export const SavedTransactions = (props: SavedTransactionsScreenProps): JSX.Element => {
  const classes = codeContainerStyles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);
  const [transactionDetail, setTransactionDetail] = useState<ITransactionListing>();
  const [payload, setPayload] = useState<IFilterTextPayload>({
    action: PosFilterAction.FilterPos,
    sameSoldChecked: false,
    value: '',
  });
  const [transactionList, setTransactionList] = useState<ITransactionListing[]>(
    props.savedTransactions,
  );

  const paginationState = usePagination({
    totalItems: transactionList.length || undefined,
    initialPageSize: pageSize,
  });

  const handleSubmit = useCallback((): void => {
    if (payload.value) {
      const filteredList = props.savedTransactions.filter(
        transaction => payload.value === transaction.touchPointId,
      );

      if (filteredList.length > 0) {
        setTransactionList(filteredList);
      } else {
        setTransactionList([]);
      }
    } else {
      setTransactionList(props.savedTransactions);
    }
  }, [payload, props.savedTransactions]);

  useEffect((): void => {
    if (payload.value === undefined || payload.value === '') {
      setTransactionList(props.savedTransactions);
    } else {
      handleSubmit();
    }
  }, [payload, props.savedTransactions, handleSubmit]);

  const onPageChange = (updatedPage: number) => {
    if (updatedPage > paginationState.currentPage + 1) {
      paginationState.setNextPage();
    } else {
      paginationState.setPreviousPage();
    }
    setTransactionDetail(undefined);
  };

  const handleOnFocus = (e): void => {
    const targetId = e.target.id;
    if (payload.action !== targetId) {
      setPayload(prevPayload => ({ ...prevPayload, action: targetId, value: '' }));
    }
  };

  const handleChange = (e): void => {
    const valueWithNumber = e.target.value.replace(/[^.0-9]/g, '').replace(/(\..*)\./g, '.');
    const value = truncateNumberWithPrecision(valueWithNumber);
    setPayload(prevPayload => ({ ...prevPayload, value }));
  };

  const handleKeypress = (event: KeyboardEvent): void => {
    if (event.keyCode === 13) {
      handleSubmit();
    }
  };

  const onSubmit = (): void => {
    props.onSubmit(transactionDetail);
  };

  const keypadSubmitButton = {
    label: 'OK',
    onClick: onSubmit,
    testId: 'recall-transaction-keypad-button',
    disabled: !transactionDetail,
  };

  const onSelect = (selectedTransaction: ITransactionListing) => {
    if (selectedTransaction.sequenceNumber === transactionDetail?.sequenceNumber) {
      setTransactionDetail(undefined);
    } else {
      setTransactionDetail(selectedTransaction);
    }
  };

  const savedTransactions = transactionList.slice(
    paginationState.currentPage * paginationState.pageSize,
    (paginationState.currentPage + 1) * paginationState.pageSize,
  );

  const listCount = transactionList.length;

  return (
    <ProductCodeContainer
      headerTitle={t('ResumeTransactionCommand')}
      onKeypadSubmit={keypadSubmitButton.onClick}
      keypadComponent={
        <Keypad
          keyboardId='recall-transaction'
          disableManualWeight
          noInputField={true}
          disableQuantity
          topLabelText={t('Enter_POS_Number')}
          submitButton={keypadSubmitButton}
          visible
          keypadTestIdPrefix='recall-transaction-'
          disableDecimalInput={true}
        />
      }
    >
      <Grid
        item
        className={`fullHeight flexFull ${classes.outerMostContainer} ${classes.containerSpacingRight}`}
        data-testid='saved-transactions'
      >
        <Grid
          item
          className={`${
            trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
          } fullWidth fullHeight`}
        >
          <Grid
            container
            direction='column'
            className={`fullHeight ${classes.outerFlexDesign}`}
            data-testid='saved-transactions-container'
          >
            <Grid
              item
              container
              className={classes.flexDesign}
              data-testid='saved-transactions-body-container'
            >
              {Object.keys(PosFilterAction).map(
                (actionType: string): JSX.Element => (
                  <FilterTextField
                    key={actionType}
                    input={payload.value}
                    action={actionType as PosFilterAction}
                    focusedAction={payload.action}
                    onFocus={handleOnFocus}
                    onChange={handleChange}
                    onKeyDown={handleKeypress}
                  />
                ),
              )}
              <Grid item container className={classes.alignBottom}>
                <Typography
                  variant='body1'
                  data-testid='transaction-item-total'
                  className={classes.topSpacing}
                >
                  {`${listCount}`} {t('Saved_Transactions')}
                </Typography>
              </Grid>
              <Grid container className={classes.contentAlign}>
                {savedTransactions.length > 0 && <SavedTransactionCategoryHead />}
                <Grid
                  container
                  item
                  direction='column'
                  data-testid='saved-transaction-list-results'
                  className={classes.listItems}
                >
                  <Grid item className={classes.tableWidth} data-testid='transaction-list'>
                    {savedTransactions.map(
                      (transaction): JSX.Element => (
                        <SavedTransactionListings
                          listing={transaction}
                          selected={
                            transaction.sequenceNumber === transactionDetail?.sequenceNumber
                          }
                          onSelect={onSelect}
                          testIdPrefix={'transaction-list-item'}
                          key={transaction.sequenceNumber}
                        />
                      ),
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item container className={classes.topPadding}>
                <Grid item container xs={4} className={classes.alignStart}>
                  <Pagination
                    currentPage={paginationState.currentPage + 1}
                    totalPages={paginationState.totalPages}
                    updateCurrentPageHandler={onPageChange}
                    displayText={true}
                    testIdPrefix={'list'}
                  />
                </Grid>
                <Grid item container xs={6}>
                  <BaseButton
                    buttonType='white'
                    data-testid={'search-button-dialog-close'}
                    className={classes.buttonSpacing}
                    onClick={props.onCancel}
                  >
                    {t('Cancel')}
                  </BaseButton>
                  <BaseButton
                    buttonType='primary'
                    data-testid={'recall-trans-add-item-btn'}
                    onClick={onSubmit}
                    disabled={transactionDetail === undefined}
                  >
                    {t('Recall').toUpperCase()}
                  </BaseButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ProductCodeContainer>
  );
};

export interface SavedTransactionsScreenProps {
  onCancel: () => void;
  onSubmit: (transactionDetail?: ITransactionListing) => Promise<void>;
  savedTransactions: ITransactionListing[];
}

export interface IFilterTextPayload {
  value: string;
  action: PosFilterAction;
  sameSoldChecked: boolean;
}
